import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import './SearchableSelect.css';

export interface PremiumSelectOption {
  value: string;
  label: string;
}

interface PremiumSelectProps {
  id?: string;
  label: string;
  value: string;
  options: PremiumSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
}

const PremiumSelect: React.FC<PremiumSelectProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  placeholder = 'Selecione...',
  required,
  disabled = false,
  wrapperClassName = 'form-group',
  inputClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const generatedId = useId().replace(/:/g, '');
  const controlId = id || `premium-select-${generatedId}`;
  const labelId = `${controlId}-label`;
  const listboxId = `${controlId}-listbox`;

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (nextValue: string): void => {
    if (disabled) return;
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className={wrapperClassName}>
      <label id={labelId}>
        {label} {required && '*'}
      </label>
      <div
        className={`searchable-select-wrapper premium-select${isOpen ? ' is-open' : ''}${
          disabled ? ' is-disabled' : ''
        }`}
        ref={wrapperRef}
      >
        <div
          id={controlId}
          className={`searchable-select-input${inputClassName ? ` ${inputClassName}` : ''}`}
          onClick={() => {
            if (disabled) return;
            setIsOpen((open) => !open);
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-labelledby={labelId}
          aria-required={required}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen((open) => !open);
            }

            if (event.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        >
          {selectedOption ? (
            <span className="searchable-select-value">{selectedOption.label}</span>
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
          <span className="arrow" aria-hidden>
            {isOpen ? '▲' : '▼'}
          </span>
        </div>

        {isOpen && (
          <div className="searchable-select-dropdown premium-select-dropdown">
            <div
              className="searchable-select-options"
              id={listboxId}
              role="listbox"
              aria-labelledby={labelId}
            >
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`searchable-select-option ${value === option.value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={value === option.value}
                >
                  <div className="option-name">{option.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumSelect;
